// Typed conciliatron API surface. Paths match the backend contract in
// internal/reconciliations/handler.go: workspace-scoped CRUD plus a dispatch
// endpoint. The reconciliation model is flat (SPEC/1573) — no instance/run
// resources; executing runs the worker and the result lands back on the
// reconciliation itself (poll GET until statusId is terminal).

import { apiClient } from './client';
import type {
  CreateReconciliationBody,
  CreateViewBody,
  PublishBody,
  Reconciliation,
  ReconciliationListResponse,
  ReconciliationView,
  SendViewBody,
  UpdateReconciliationBody,
  UpdateViewBody,
  ViewListResponse,
} from '../types/api';

// ── Reconciliations (workspace-scoped CRUD) ──────────────────────────
export const listReconciliations = (workspaceId: number): Promise<ReconciliationListResponse> =>
  apiClient.get(`/api/v1/workspaces/${workspaceId}/reconciliations`);

export const createReconciliation = (
  workspaceId: number,
  body: CreateReconciliationBody
): Promise<Reconciliation> =>
  apiClient.post(`/api/v1/workspaces/${workspaceId}/reconciliations`, body);

export const getReconciliation = (workspaceId: number, id: number): Promise<Reconciliation> =>
  apiClient.get(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}`);

export const updateReconciliation = (
  workspaceId: number,
  id: number,
  body: UpdateReconciliationBody
): Promise<Reconciliation> =>
  apiClient.patch(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}`, body);

export const deleteReconciliation = (workspaceId: number, id: number): Promise<void> =>
  apiClient.delete(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}`);

// ── Execution (dispatch the worker) ──────────────────────────────────
/**
 * Empty-body POST. Dispatches the reconciliation's config to the worker and
 * returns the reconciliation snapshot (statusId moves off New). Poll
 * `getReconciliation` until `isTerminalStatus(statusId)` to read the result.
 *
 * With `instanceIds`, runs only those charts and merges them into the stored
 * result (Bug 2609). The status does not move for that one — poll until
 * `pendingInstanceIds` is gone (lib/partialRun).
 */
export const executeReconciliation = (
  workspaceId: number,
  id: number,
  instanceIds?: string[]
): Promise<Reconciliation> =>
  instanceIds?.length
    ? apiClient.post(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}/execute`, {
        instanceIds,
      })
    : apiClient.post(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}/execute`);

// ── Publishing and views (SPEC/2102 §5) ───────────────────────────────────
//
// Publishing and sending are POSTs to their own paths, not a PATCH carrying a
// flag. The surface they replace expressed publishing as PATCH {published:true}
// and then rejected every other PATCH body as invalid input — a verb pretending
// to be an action. RBAC also has to be able to name them by path.

/**
 * Seal the reconciliation and open its view #1, in one backend transaction.
 * Irreversible (D1): from here neither the config nor the result can change.
 * 409 when it is already published or when it never executed (D6).
 */
export const publishReconciliation = (
  workspaceId: number,
  id: number,
  body?: PublishBody
): Promise<Reconciliation> =>
  apiClient.post(`/api/v1/workspaces/${workspaceId}/reconciliations/${id}/publish`, body ?? {});

const viewsPath = (workspaceId: number, reconciliationId: number) =>
  `/api/v1/workspaces/${workspaceId}/reconciliations/${reconciliationId}/views`;

/** The reconciliation's views, oldest first: items[0] is the one publishing
 *  opened. It used to be safe to call it "Vista 1" — nothing sent a name at
 *  publish time — and since the confirmation dialog does, its name is whatever the
 *  person chose. The ORDER is the invariant, not the name. */
export const listViews = (
  workspaceId: number,
  reconciliationId: number
): Promise<ViewListResponse> => apiClient.get(viewsPath(workspaceId, reconciliationId));

export const getView = (
  workspaceId: number,
  reconciliationId: number,
  viewId: number
): Promise<ReconciliationView> =>
  apiClient.get(`${viewsPath(workspaceId, reconciliationId)}/${viewId}`);

/** Another presentation of the same sealed numbers. With `copyFrom` it
 *  duplicates a view's shape, notes and report — the way to keep working
 *  after one has been sent and frozen (D5). */
export const createView = (
  workspaceId: number,
  reconciliationId: number,
  body: CreateViewBody
): Promise<ReconciliationView> =>
  apiClient.post(viewsPath(workspaceId, reconciliationId), body);

/** Sparse: only the fields present are applied. 409 once the view is sent. */
export const updateView = (
  workspaceId: number,
  reconciliationId: number,
  viewId: number,
  body: UpdateViewBody
): Promise<ReconciliationView> =>
  apiClient.put(`${viewsPath(workspaceId, reconciliationId)}/${viewId}`, body);

/**
 * Record the uploaded PDF and the recipients, and freeze the view.
 *
 * Nothing in the system sends mail yet (PR 5) — this is a person asserting the
 * report went out, which is why the UI calls it "Marcar como enviada".
 */
export const sendView = (
  workspaceId: number,
  reconciliationId: number,
  viewId: number,
  body: SendViewBody
): Promise<ReconciliationView> =>
  apiClient.post(`${viewsPath(workspaceId, reconciliationId)}/${viewId}/send`, body);

/** Drafts only — a sent view is 409 (D11). */
export const deleteView = (
  workspaceId: number,
  reconciliationId: number,
  viewId: number
): Promise<void> =>
  apiClient.delete(`${viewsPath(workspaceId, reconciliationId)}/${viewId}`);
